import type { FirmContactView } from "../interfaces/models/IFirmContact";
import { getOfficeById, updateOffice } from "../Services/Office";
import { saveFirmContact } from "../Services/FirmContact";
import { toFirmContactPayload } from "./firmContactUtils";
import type { AccountingOfficeDetail } from "./accountingOfficeDetail";
import { parseCalendarDate } from "./dates";

export type AccountingParametersForm = {
  legalName: string;
  accountingContactId: string;
  ccContactId: string;
  address1: string;
  city: string;
  state: string;
  country: string;
  dateJoinedDisplay: string;
  dateWithdrawnDisplay: string;
  billingStartDateDisplay: string;
  jurisdictionSize: string;
};

type SaveAccountingParametersInput = {
  office: AccountingOfficeDetail;
  firmContact: FirmContactView | null;
  form: AccountingParametersForm;
  isSecondary: boolean;
};

function optionalDisplayToIso(display: string): string {
  const trimmed = display.trim();
  if (!trimmed) {
    return "";
  }

  const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (mmddyyyy) {
    const month = mmddyyyy[1].padStart(2, "0");
    const day = mmddyyyy[2].padStart(2, "0");
    return `${mmddyyyy[3]}-${month}-${day}`;
  }

  const parsed = parseCalendarDate(trimmed);
  if (!parsed) {
    return "";
  }
  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const d = String(parsed.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isoToDate(iso: string): Date | undefined {
  if (!iso) {
    return undefined;
  }
  const parsed = parseCalendarDate(iso);
  return parsed ?? undefined;
}

export async function saveAccountingParameters({
  office,
  firmContact,
  form,
  isSecondary,
}: SaveAccountingParametersInput): Promise<void> {
  const joinedIso = optionalDisplayToIso(form.dateJoinedDisplay);
  const withdrawnIso = optionalDisplayToIso(form.dateWithdrawnDisplay);
  const billingStartIso = optionalDisplayToIso(form.billingStartDateDisplay);
  const jurisdictionSize = String(form.jurisdictionSize ?? "").trim();
  const professionalsInPrimary = Number(jurisdictionSize);

  const rawOffice = await getOfficeById(office.id);
  if (!rawOffice?.id) {
    throw new Error("Office not found");
  }

  await updateOffice({
    ...rawOffice,
    firmLegalName: form.legalName.trim() || rawOffice.firmLegalName,
    firmName: form.legalName.trim() || rawOffice.firmName,
    address: form.address1.trim() || rawOffice.address,
    cityName: form.city.trim() || rawOffice.cityName,
    stateName: form.state.trim() || rawOffice.stateName,
    countryName: form.country.trim() || rawOffice.countryName,
    dateJoined: isoToDate(joinedIso) ?? rawOffice.dateJoined,
    dateWithdrawn: withdrawnIso ? isoToDate(withdrawnIso) : undefined,
    billingStartDate: billingStartIso || rawOffice.billingStartDate,
    firmProfessionalsInPrimary: jurisdictionSize,
    professionalsInPrimary: Number.isFinite(professionalsInPrimary)
      ? professionalsInPrimary
      : rawOffice.professionalsInPrimary,
  });

  const accountingContactId = form.accountingContactId.trim();
  const ccContactId = String(form.ccContactId ?? "").trim();
  const shouldPersistFirmContact =
    Boolean(ccContactId) ||
    (!isSecondary && Boolean(accountingContactId));

  if (!shouldPersistFirmContact) {
    return;
  }

  if (firmContact) {
    const payload = toFirmContactPayload(
      firmContact,
      String(firmContact.id ?? "").trim() || undefined,
    );
    if (!isSecondary) {
      payload.accountingContactId = accountingContactId;
    }
    payload.ccContactId = ccContactId || undefined;
    await saveFirmContact(payload);
    return;
  }

  const firmId = String(office.firm ?? "").trim();
  if (!firmId) {
    throw new Error("Missing firm id");
  }

  await saveFirmContact({
    firmId,
    officeId: office.id,
    primaryContactId: "",
    secondaryContactId: "",
    accountingContactId: isSecondary ? "" : accountingContactId,
    ccContactId: ccContactId || undefined,
  });
}
